import { UseMutationOptions, useMutation, useQueryClient } from 'react-query';

import { fetchJson } from '@zapier/toolbox-browser-fetch';

import { ZAPIER_APP_BASE_URL } from 'app/common/constants';
import { useNotify } from 'app/developer-v3/hooks';
import { getQueryKey } from './useQueryIntegrationVersionConfig';

import { formatIntegrationVersionConfig } from './utils';

import type { IntegrationVersionConfig } from './types';

const MUTATION_KEY = 'mutate-update-publishing-form';

export function formatResponseBody(body: any): IntegrationVersionConfig {
  return formatIntegrationVersionConfig(body);
}

function formatToRequestBody(
  integrationVersionConfig: IntegrationVersionConfig
) {
  const { isStaticIPEnabled } = integrationVersionConfig;
  return { allow_static_ip: isStaticIPEnabled };
}

type Args = IntegrationVersionConfig;

function useMutationIntegrationVersionConfig(
  args: { integrationId: number; integrationVersion: string },
  options?: UseMutationOptions<void, Error, Args, IntegrationVersionConfig>
) {
  const { integrationId, integrationVersion } = args;
  const notify = useNotify();

  const queryClient = useQueryClient();
  const queryKey = getQueryKey({ integrationId, integrationVersion });
  const mutationKey = MUTATION_KEY;
  const url = new URL(
    `api/platform/cli/apps/${encodeURIComponent(
      integrationId
    )}/versions/${encodeURIComponent(integrationVersion)}/config`,
    ZAPIER_APP_BASE_URL
  );

  const mutation = useMutation({
    mutationKey,
    async mutationFn(integrationVersionConfig: Args) {
      try {
        const response = await fetchJson(url.href, {
          body: JSON.stringify(formatToRequestBody(integrationVersionConfig)),
          method: 'PATCH',
        });
        return response;
      } catch (error) {
        throw formatResponseBody(JSON.parse(error.responseText));
      }
    },
    onError: (error: any, _variables) => {
      notify.failure(
        'There was a problem updating the integration version config.',
        mutationKey
      );
      console.error(JSON.stringify(error));
    },
    onSuccess: () => {
      notify.success('The integration version config has been updated.');
      queryClient.invalidateQueries(queryKey);
    },
    ...options,
  });

  return { ...mutation, mutationKey };
}

export default useMutationIntegrationVersionConfig;
